<template>
    <v-dialog :value="visible" max-width="980" scrollable persistent>
        <v-card v-if="informe">
            <v-card-title class="text-subtitle-1 font-weight-bold">
                <v-icon left :color="hayRechazadas ? 'warning' : 'success'">{{ hayRechazadas ? 'report_problem' : 'verified' }}</v-icon>
                {{ esRevision ? 'Revisión del archivo' : 'Resultado de la importación' }}
            </v-card-title>

            <v-card-text>
                <!-- Resumen -->
                <div class="resumen mb-3" v-if="resumen">
                    <div class="resumen__dato"><b>{{ resumen.filas }}</b><span>filas en el Excel</span></div>
                    <div class="resumen__dato resumen__dato--bien"><b>{{ resumen.validas }}</b><span>{{ esRevision ? 'se importarían' : 'importadas' }}</span></div>
                    <div class="resumen__dato" :class="{'resumen__dato--mal': resumen.rechazadas}"><b>{{ resumen.rechazadas }}</b><span>rechazadas</span></div>
                    <div class="resumen__dato" :class="{'resumen__dato--aviso': resumen.avisos}"><b>{{ resumen.avisos }}</b><span>avisos</span></div>
                    <div class="resumen__dato"><b>{{ resumen.participantes_nuevos }}</b><span>participantes nuevos</span></div>
                    <div class="resumen__dato"><b>{{ resumen.participantes_existentes }}</b><span>ya registrados</span></div>
                </div>

                <v-alert dense text :type="tipoAlerta" class="mb-3">
                    <template v-if="esRevision && !hayRechazadas">El archivo está listo: todas las filas pasaron la revisión. No se ha guardado nada todavía.</template>
                    <template v-else-if="esRevision">
                        No se ha guardado nada. Puede corregir el Excel y revisarlo de nuevo, o importar ahora solo las
                        <b>{{ resumen.validas }}</b> filas válidas y subir las demás después.
                    </template>
                    <template v-else-if="resumen && resumen.validas">
                        Se importaron <b>{{ resumen.validas }}</b> filas. Las rechazadas quedaron fuera: corríjalas en el
                        Excel y súbalas en una importación aparte.
                    </template>
                    <template v-else>No se importó ninguna fila. Corrija el Excel y vuelva a intentarlo.</template>
                </v-alert>

                <v-tabs v-model="pestana" grow v-if="hayRechazadas || hayAvisos">
                    <v-tab>Rechazadas ({{ informe.rechazadas.length }})</v-tab>
                    <v-tab>Avisos ({{ informe.avisos.length }})</v-tab>
                </v-tabs>

                <p class="text-caption grey--text text--darken-1 mt-3 mb-2" v-if="hayRechazadas || hayAvisos">
                    {{ pestana === 0
                        ? 'Estas filas no entran a la base de datos.'
                        : 'Estas filas sí entran; el sistema corrigió o aceptó algo que conviene revisar.' }}
                </p>

                <v-data-table
                    v-if="hayRechazadas || hayAvisos"
                    dense class="elevation-1"
                    :headers="columnas"
                    :items="pestana === 0 ? informe.rechazadas : informe.avisos"
                    :items-per-page="15"
                    :footer-props="{'items-per-page-options': [15, 50, 100, -1], 'items-per-page-text': 'Filas'}"
                >
                    <template v-slot:no-data>
                        <v-alert dense type="success" outlined class="text-center mt-3">Ninguna.</v-alert>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-card-actions>
                <v-btn small text color="primary" @click="copiar" v-if="hayRechazadas || hayAvisos">
                    <v-icon small left>content_copy</v-icon> Copiar lista
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn small text @click="$emit('cerrar')">{{ esRevision ? 'Volver al archivo' : 'Entendido' }}</v-btn>
                <v-btn small color="primary" v-if="esRevision && resumen && resumen.validas" @click="$emit('importar')">
                    <v-icon small left>upload_file</v-icon>
                    Importar {{ resumen.validas }} {{ resumen.validas === 1 ? 'fila válida' : 'filas válidas' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/**
 * Informe de la revisión previa o del resultado de una importación:
 * resumen, filas rechazadas con su motivo y avisos.
 */
export default {
    name: "InformeRevision",
    props: {
        visible: {type: Boolean, default: false},
        // {resumen, rechazadas: [{fila, numero_identidad, motivo}], avisos: [{fila, numero_identidad, aviso}]}
        informe: {type: Object, default: null},
        // "revision" antes de importar; "importacion" después.
        modo: {type: String, default: "revision"},
    },
    data() {
        return {pestana: 0};
    },
    computed: {
        esRevision() {
            return this.modo === "revision";
        },
        resumen() {
            return this.informe ? this.informe.resumen : null;
        },
        hayRechazadas() {
            return !!(this.informe && this.informe.rechazadas.length);
        },
        hayAvisos() {
            return !!(this.informe && this.informe.avisos.length);
        },
        tipoAlerta() {
            if (!this.hayRechazadas) return "success";
            return this.resumen && this.resumen.validas ? "info" : "error";
        },
        columnas() {
            return [
                {text: 'Fila del Excel', value: 'fila', width: 120},
                {text: 'Identidad', value: 'numero_identidad', width: 170},
                ...(this.pestana === 0 ? [{text: 'Columna', value: 'columna', width: 150}] : []),
                {text: this.pestana === 0 ? 'Motivo' : 'Aviso', value: this.pestana === 0 ? 'motivo' : 'aviso', sortable: false},
            ];
        },
    },
    watch: {
        visible(abierto) {
            if (abierto) this.pestana = this.hayRechazadas || !this.hayAvisos ? 0 : 1;
        },
    },
    methods: {
        copiar() {
            const filas = this.pestana === 0
                ? this.informe.rechazadas.map(r => `Fila ${r.fila}\t${r.numero_identidad}\t${r.columna || ''}\t${r.motivo}`)
                : this.informe.avisos.map(r => `Fila ${r.fila}\t${r.numero_identidad}\t${r.aviso}`);

            if (navigator.clipboard) {
                navigator.clipboard.writeText(filas.join("\n")).then(() => this.$toast.success("Lista copiada."));
            }
        },
    },
}
</script>

<style scoped>
.resumen {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
}

.resumen__dato {
    border: 1px solid #e3e7f1;
    border-radius: 6px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.resumen__dato b {
    font-size: 1.35rem;
    color: #1b2340;
    font-variant-numeric: tabular-nums;
}

.resumen__dato span {
    font-size: 0.75rem;
    color: #5b6380;
}

.resumen__dato--bien b { color: #2e7d32; }
.resumen__dato--mal b { color: #c62828; }
.resumen__dato--aviso b { color: #b26a00; }
</style>
